(() => {
  const ROOT_SELECTOR = '[data-fe-onboarding]';
  const SUBMIT_STATE_KEY = 'fe-voranmeldung-last-submit';
  const SUBMISSION_ID_KEY = 'fe-voranmeldung-submission-id-v2';
  const LEGACY_SUBMISSION_ID_KEY = 'fe-voranmeldung-submission-id-v1';
  const SUBMISSION_ID_MAX_AGE_MS = 12 * 60 * 60 * 1000;

  class FeVoranmeldung {
    constructor(root) {
      this.root = root;
      this.startScreen = root.querySelector('[data-fe-start]');
      this.flow = root.querySelector('[data-fe-flow]');
      this.form = root.querySelector('#fe-voranmeldung-form');
      this.completeScreen = root.querySelector('[data-fe-complete]');
      this.steps = [...root.querySelectorAll('[data-fe-step]')];
      this.dots = [...root.querySelectorAll('[data-fe-progress-dot]')];
      this.stepLabel = root.querySelector('[data-fe-step-label]');
      this.backButton = root.querySelector('[data-fe-back]');
      this.nextButton = root.querySelector('[data-fe-next]');
      this.config = this.readConfig();
      this.routes = this.buildRoutes();
      this.currentStep = 1;
      this.isMinor = false;
      this.isSubmitting = false;
      this.submissionId = this.restoreSubmissionId();
      this.startedAt = Date.now();
      this.vehicleLightTimer = null;
      this.sentMakeStatuses = new Set();

      if (!this.startScreen || !this.flow || !this.form || !this.nextButton) return;

      this.hydrateStaticVideos();
      this.bindEvents();
      this.configureDates();
      this.syncVariantPanels();
    }

    readConfig() {
      try {
        const config = JSON.parse(this.root.querySelector('[data-fe-config]')?.textContent || '{}');
        config.priceOverrides ||= {};
        config.products ||= {};
        this.root.querySelectorAll('[data-fe-price-profile]').forEach((node) => {
          try {
            const profile = JSON.parse(node.textContent || '{}');
            const key = String(profile.key || '').trim();
            if (!key) return;
            config.priceOverrides[key] = profile.override || {};
            if (Number(profile.product?.variantId || 0) > 0) config.products[key] = profile.product;
          } catch (_) {
            // Eine einzelne fehlerhafte Editor-Konfiguration blockiert nicht den übrigen Ablauf.
          }
        });

        const labels = {
          base: 'Grundbetrag',
          app: 'Lern-App',
          theory: 'Vorstellung zur Theorieprüfung',
          lesson: 'Fahrstunde je 45 Min.',
          lessonAfter: 'Weitere Fahrstunde',
          control: 'Abfahrtskontrollkarte',
          instruction: 'Unterweisung',
          special: 'Sonderfahrt je 45 Min.',
          trailerInstruction: 'Unterweisung / Einweisung Anhänger',
          lessonCe: 'Übungsfahrt Klasse CE je 45 Min.',
          specialCe: 'Sonderfahrt Klasse CE je 45 Min.',
          practical: 'Vorstellung zur praktischen Prüfung',
          practicalCe: 'Vorstellung praktische Prüfung Klasse CE',
          repeatTheory: 'Wiederholungsprüfung Theorie',
          repeatPractical: 'Wiederholungsprüfung Praxis',
          repeatControl: 'Wiederholungsprüfung Abfahrtskontrollkarte'
        };

        Object.entries(config.priceOverrides || {}).forEach(([routeKey, override]) => {
          if (!routeKey || !config.prices?.[routeKey]) return;
          const price = config.prices[routeKey];
          price.rows ||= [];

          Object.entries(labels).forEach(([role, label]) => {
            const value = String(override?.[role] || '').trim();
            if (!value) return;
            let row = price.rows.find((entry) => entry.role === role);
            if (!row) {
              row = { role, label, value: '' };
              price.rows.push(row);
            }
            row.value = value;
            if (role === 'special' || role === 'specialCe') {
              row.isSpecial = true;
              row.specialText =
                String((role === 'specialCe' ? override.specialInfoCe : override.specialInfo) || '').trim() ||
                row.specialText ||
                'Die genaue Anzahl und Aufteilung richtet sich nach der gewählten Fahrerlaubnisklasse.';
            }
          });

          const base = String(override?.base || '').trim();
          const promoBase = String(override?.promoBase || '').trim();
          const total = String(override?.total || '').trim();
          const totalNote = String(override?.totalNote || '').trim();
          if (base) price.base = base;
          if (promoBase) price.promoBase = promoBase;
          if (promoBase) price.promoLabel = String(override?.promoLabel || 'Aktions-Angebot').trim();
          if (total) price.total = total;
          if (totalNote) price.totalNote = totalNote;

          const baseRow = price.rows.find((entry) => entry.role === 'base');
          if (baseRow && promoBase) baseRow.promoValue = promoBase;
        });

        return config;
      } catch (_) {
        return { links: {}, products: {}, prices: {} };
      }
    }

    hydrateStaticVideos() {
      this.root.querySelectorAll('[data-fe-video-key]').forEach((slot) => {
        this.fillVideoSlot(slot, slot.dataset.feVideoKey);
      });
    }

    fillVideoSlot(slot, key) {
      if (!slot || !key || slot.dataset.feCurrentVideoKey === key) return;
      const template = [...this.root.querySelectorAll('[data-fe-video-template]')]
        .find((item) => item.dataset.feVideoTemplate === key);
      slot.replaceChildren();
      if (template) {
        slot.appendChild(template.content.cloneNode(true));
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'fe-video-shell fe-video-shell--placeholder';
        placeholder.dataset.feVideoShell = '';
        placeholder.dataset.feHasVideo = 'false';
        placeholder.innerHTML = '<span class="fe-video-caption">Erklärvideo</span><span class="fe-video-missing">Video im Theme-Editor auswählen</span>';
        slot.appendChild(placeholder);
      }
      slot.dataset.feCurrentVideoKey = key;
      this.bindVideoElements(slot);
    }

    setVideoSlot(name, key) {
      const slot = [...this.root.querySelectorAll('[data-fe-video-slot]')]
        .find((item) => item.dataset.feVideoSlot === name);
      this.fillVideoSlot(slot, key);
    }

    bindVideoElements(scope = this.root) {
      scope.querySelectorAll('[data-fe-video-play]').forEach((button) => {
        if (button.dataset.feBound === 'true') return;
        button.dataset.feBound = 'true';
        button.addEventListener('click', () => this.playVideo(button.closest('[data-fe-video-shell]')));
      });
      scope.querySelectorAll('[data-fe-video]').forEach((video) => {
        if (video.dataset.feBound === 'true') return;
        video.dataset.feBound = 'true';
        video.addEventListener('ended', () => {
          video.closest('[data-fe-video-shell]')?.classList.remove('is-playing');
        });
        video.addEventListener('pause', () => {
          if (!video.ended) return;
          video.closest('[data-fe-video-shell]')?.classList.remove('is-playing');
        });
      });
    }

    buildRoutes() {
      const checkout = (label, category, detail, price, product, checklist = 'standard', extra = {}) => ({
        label,
        category,
        detail,
        price,
        product,
        checklist,
        kind: 'checkout',
        recipient: 'info',
        ...extra
      });
      const contact = (label, category, detail, price, checklist, extra = {}) => ({
        label,
        category,
        detail,
        price,
        checklist,
        kind: 'contact',
        recipient: 'professional',
        intro: 'Wir prüfen deine Auswahl und melden uns persönlich mit den passenden nächsten Schritten bei dir.',
        ...extra
      });

      return {
        b_schalter: checkout(
          'Klasse B – Schaltung',
          'PKW',
          'b_schalter',
          'b197',
          'b197',
          'standard',
          { intro: 'Du absolvierst Ausbildung und Prüfung auf einem Schaltfahrzeug und darfst danach Schalt- und Automatikautos fahren.' }
        ),
        b_automatik: checkout(
          'Klasse B – Automatik',
          'PKW',
          'b_automatik',
          'b197',
          'b197',
          'standard',
          { intro: 'Du fährst und prüfst auf Automatik. Die Schlüsselzahl 78 beschränkt die Fahrerlaubnis anschließend auf Automatikfahrzeuge.' }
        ),
        b197: checkout(
          'Klasse B197',
          'PKW',
          'b197',
          'b197',
          'b197',
          'standard',
          { intro: 'Du lernst auf Schalt- und Automatikfahrzeugen und legst deine praktische Prüfung auf einem Automatikfahrzeug ab.' }
        ),
        b_schalter_intensive: checkout('Klasse B – Schaltung als Intensivkurs', 'PKW', 'pkw_intensive', 'b197_intensive', 'b197_intensive', 'standard', {
          intro: 'Deine Schaltausbildung wird in einem kompakten, persönlich geplanten Zeitraum organisiert.'
        }),
        b_automatik_intensive: checkout('Klasse B – Automatik als Intensivkurs', 'PKW', 'pkw_intensive', 'b197_intensive', 'b197_intensive', 'standard', {
          intro: 'Deine Automatikausbildung wird in einem kompakten, persönlich geplanten Zeitraum organisiert.'
        }),
        b197_intensive: checkout('Klasse B197 als Intensivkurs', 'PKW', 'b197_intensive', 'b197_intensive', 'b197_intensive', 'standard', {
          intro: 'Die Inhalte der B197-Ausbildung werden in einem kompakten, persönlich geplanten Zeitraum organisiert.'
        }),
        b96: checkout('Schlüsselzahl B96', 'PKW', 'b96', 'b96', 'b96', 'b96', {
          prerequisite: 'Ja, ich habe bereits die Fahrerlaubnis Klasse B.',
          payInFull: true,
          intro: 'B96 erweitert Klasse B für größere Fahrzeug-Anhänger-Kombinationen – ohne zusätzliche Prüfung.'
        }),
        be: checkout('Klasse BE', 'PKW', 'be', 'be', 'be', 'be', {
          prerequisite: 'Ja, ich habe bereits die Fahrerlaubnis Klasse B.',
          intro: 'Klasse BE ist die eigenständige Anhänger-Fahrerlaubnis mit praktischer Prüfung.'
        }),

        a_standard: checkout('Klasse A – regulär', 'Motorrad', 'a', 'motorcycle_standard', 'motorcycle_standard', 'standard', {
          intro: 'Mit Klasse A fährst du Motorräder ohne Leistungsbeschränkung.'
        }),
        a_intensive: checkout('Klasse A – Intensivkurs', 'Motorrad', 'a', 'motorcycle_intensive', 'motorcycle_intensive', 'standard', {
          prerequisite: 'Ja, ich besitze bereits die Fahrerlaubnis Klasse B.',
          detailTitle: 'Kompakte Intensivausbildung – Führerschein in 14 Tagen möglich'
        }),
        a_stage: checkout('Stufenaufstieg A2 → A', 'Motorrad', 'a', 'motorcycle_stage', 'motorcycle_stage', 'standard', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse A2 seit mindestens zwei Jahren.',
          intro: 'Beim Stufenaufstieg ist keine Theorieprüfung und keine gesetzliche Mindestzahl an Fahrstunden vorgeschrieben.'
        }),
        a2_standard: checkout('Klasse A2 – regulär', 'Motorrad', 'a2', 'motorcycle_standard', 'motorcycle_standard', 'standard', {
          intro: 'Mit Klasse A2 fährst du Motorräder bis 35 kW.'
        }),
        a2_intensive: checkout('Klasse A2 – Intensivkurs', 'Motorrad', 'a2', 'motorcycle_intensive', 'motorcycle_intensive', 'standard', {
          prerequisite: 'Ja, ich besitze bereits die Fahrerlaubnis Klasse B.',
          detailTitle: 'Kompakte Intensivausbildung – Führerschein in 14 Tagen möglich'
        }),
        a2_stage: checkout('Stufenaufstieg A1 → A2', 'Motorrad', 'a2', 'motorcycle_stage', 'motorcycle_stage', 'standard', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse A1 seit mindestens zwei Jahren.',
          intro: 'Beim Stufenaufstieg ist keine Theorieprüfung und keine gesetzliche Mindestzahl an Fahrstunden vorgeschrieben.'
        }),
        a1: checkout('Klasse A1', 'Motorrad', 'a1', 'a1', 'a1', 'standard', {
          intro: 'Klasse A1 ist dein Einstieg in die Welt der 125er-Motorräder.'
        }),
        b196: checkout('Schlüsselzahl B196', 'Motorrad', 'b196', 'b196', 'b196', 'b196', {
          prerequisite: 'Ja, ich bin mindestens 25 Jahre alt und besitze Klasse B seit mindestens fünf Jahren.',
          intro: 'Mit B196 kannst du in Deutschland 125er-Motorräder fahren – ohne theoretische oder praktische Prüfung.'
        }),
        am: checkout('Klasse AM', 'Motorrad', 'am', 'am', 'am', 'standard', {
          intro: 'Klasse AM ist der Einstieg für Roller, Mopeds und leichte Fahrzeuge bis 45 km/h.'
        }),
        mofa: checkout('Mofa-Prüfbescheinigung', 'Motorrad', 'mofa', 'mofa', 'mofa', 'mofa', {
          intro: 'Mit der Mofa-Prüfbescheinigung kannst du einsitzige Mofas bis 25 km/h fahren.'
        }),

        lkw_c_b: contact('Klasse C mit Vorbesitz B', 'LKW', 'lkw', 'lkw_c_b', 'lkw', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse B.'
        }),
        lkw_c_c1: contact('Klasse C mit Vorbesitz C1', 'LKW', 'lkw', 'lkw_c_c1', 'lkw', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C1.'
        }),
        lkw_ce_c: contact('Klasse CE mit Vorbesitz C', 'LKW', 'lkw', 'lkw_ce_c', 'lkw', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C.'
        }),
        lkw_cce_b: contact('Klasse C + CE mit Vorbesitz B', 'LKW', 'lkw', 'lkw_cce_b', 'lkw', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse B.'
        }),
        lkw_cce_c1: contact('Klasse C + CE mit Vorbesitz C1', 'LKW', 'lkw', 'lkw_cce_c1', 'lkw', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C1.'
        }),
        lkw_c1e_c1: contact('Klasse C1E mit Vorbesitz C1', 'LKW', 'lkw', 'lkw_c1e_c1', 'lkw', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C1.'
        }),
        lkw_conversion: contact('Umschreibung C + CE', 'LKW', 'conversion', 'lkw_conversion', 'conversion', {
          foreignLicense: true
        }),
        bus_d_b_long: contact('Klasse D · B länger als zwei Jahre', 'Bus', 'bus', 'bus_d_b_long', 'bus', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse B seit länger als zwei Jahren.'
        }),
        bus_d_b_short: contact('Klasse D · B höchstens zwei Jahre', 'Bus', 'bus', 'bus_d_b_short', 'bus', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse B seit höchstens zwei Jahren.'
        }),
        bus_d_c1_long: contact('Klasse D · C1 länger als zwei Jahre', 'Bus', 'bus', 'bus_d_c1_long', 'bus', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C1 seit länger als zwei Jahren.',
          intro: 'Bei Vorbesitz C1 gehören 12 klassenspezifische Theorie-Doppelstunden zur Ausbildung.'
        }),
        bus_d_c1_short: contact('Klasse D · C1 höchstens zwei Jahre', 'Bus', 'bus', 'bus_d_c1_short', 'bus', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C1 seit höchstens zwei Jahren.',
          intro: 'Bei Vorbesitz C1 gehören 12 klassenspezifische Theorie-Doppelstunden zur Ausbildung.'
        }),
        bus_d_c_long: contact('Klasse D · C länger als zwei Jahre', 'Bus', 'bus', 'bus_d_c_long', 'bus', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C seit länger als zwei Jahren.'
        }),
        bus_d_c_short: contact('Klasse D · C höchstens zwei Jahre', 'Bus', 'bus', 'bus_d_c_short', 'bus', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse C seit höchstens zwei Jahren.'
        }),
        bus_de_d: contact('Klasse DE mit Vorbesitz D', 'Bus', 'bus', 'bus_de_d', 'bus', {
          prerequisite: 'Ja, ich besitze die Fahrerlaubnis Klasse D.',
          intro: 'Für DE sind vier Stunden Grundausbildung sowie 3× Überland, 1× Autobahn und 1× Nachtfahrt vorgesehen; eine Theorieprüfung ist nicht erforderlich.'
        }),

        gq_goods_regular: contact('Beschleunigte Grundqualifikation Güterverkehr', 'Grundqualifikation', 'qualification', 'gq_regular', 'qualification'),
        gq_goods_career_change: contact('Grundqualifikation Güterverkehr für Umsteiger', 'Grundqualifikation', 'qualification', 'gq_career_change', 'qualification', {
          prerequisite: 'Ja, ich besitze bereits eine gültige Grundqualifikation im Personenverkehr.'
        }),
        gq_goods_unsure: contact('Beratung zur Grundqualifikation Güterverkehr', 'Grundqualifikation', 'qualification', null, 'qualification'),
        gq_people_regular: contact('Beschleunigte Grundqualifikation Personenverkehr', 'Grundqualifikation', 'qualification', 'gq_regular', 'qualification'),
        gq_people_career_change: contact('Grundqualifikation Personenverkehr für Umsteiger', 'Grundqualifikation', 'qualification', 'gq_career_change', 'qualification', {
          prerequisite: 'Ja, ich besitze bereits eine gültige Grundqualifikation im Güterverkehr.'
        }),
        gq_people_unsure: contact('Beratung zur Grundqualifikation Personenverkehr', 'Grundqualifikation', 'qualification', null, 'qualification')
      };
    }

    bindEvents() {
      this.root.querySelector('[data-fe-start-button]')?.addEventListener('click', () => {
        this.startFlow(1);
      });

      this.backButton?.addEventListener('click', () => this.goBack());
      this.nextButton?.addEventListener('click', () => this.goForward());

      this.root.querySelectorAll('[data-fe-category]').forEach((input) => {
        input.addEventListener('change', (event) => this.selectCategory(event.currentTarget));
      });

      this.root.querySelectorAll('input[name="fe-pkw-route"]').forEach((input) => {
        input.addEventListener('change', () => {
          this.syncChoiceStyles(input.closest('fieldset'));
          this.syncPkwOptions();
          this.syncPrerequisite();
          this.updateNavigation();
        });
      });
      this.root.querySelector('[data-fe-pkw-intensive]')?.addEventListener('change', () => {
        this.syncPrerequisite();
        this.updateNavigation();
      });
      this.root.querySelector('[data-fe-bf17]')?.addEventListener('change', () => this.updateNavigation());

      this.root.querySelectorAll('input[name="fe-motorcycle-class"]').forEach((input) => {
        input.addEventListener('change', () => this.selectMotorcycleClass(input));
      });

      this.root.querySelectorAll('input[name="fe-motorcycle-training"]').forEach((input) => {
        input.addEventListener('change', () => {
          this.syncChoiceStyles(input.closest('fieldset'));
          if (input.value === 'stage' && input.checked) {
            const intensive = this.root.querySelector('[data-fe-motorcycle-intensive]');
            if (intensive) intensive.checked = false;
          }
          this.syncMotorcycleOptions();
          this.syncPrerequisite();
          this.updateNavigation();
        });
      });
      this.root.querySelector('[data-fe-motorcycle-intensive]')?.addEventListener('change', () => {
        this.syncPrerequisite();
        this.updateNavigation();
      });

      this.root.querySelectorAll('input[name="fe-lkw-route"], input[name="fe-bus-route"]').forEach((input) => {
        input.addEventListener('change', () => {
          if (input.name === 'fe-lkw-route' && input.checked) this.clearLkwTraining();
          if (input.name === 'fe-bus-route' && input.checked) this.clearBusTraining();
          this.syncChoiceStyles(input.closest('fieldset'));
          this.syncHeavyOptions();
          this.syncPrerequisite();
          this.updateNavigation();
        });
      });

      this.root.querySelectorAll('[data-fe-lkw-training], [data-fe-lkw-license]').forEach((select) => {
        select.addEventListener('change', () => {
          if (select.matches('[data-fe-lkw-training]') && select.value) {
            this.root.querySelectorAll('input[name="fe-lkw-route"]').forEach((input) => {
              input.checked = false;
            });
            this.syncChoiceStyles(this.root.querySelector('[data-fe-variant-panel="lkw"] .fe-heavy-qualification'));
          }
          this.syncLkwSelection();
          this.syncHeavyOptions();
          this.syncPrerequisite();
          this.updateNavigation();
        });
      });

      this.root.querySelectorAll('[data-fe-bus-training], [data-fe-bus-license], [data-fe-bus-duration]').forEach((select) => {
        select.addEventListener('change', () => {
          if (select.matches('[data-fe-bus-training]') && select.value) {
            this.root.querySelectorAll('input[name="fe-bus-route"]').forEach((input) => {
              input.checked = false;
            });
            this.syncChoiceStyles(this.root.querySelector('[data-fe-variant-panel="bus"] .fe-heavy-qualification'));
          }
          this.syncBusSelection();
          this.syncHeavyOptions();
          this.syncPrerequisite();
          this.updateNavigation();
        });
      });

      this.root.querySelectorAll('input[name^="fe-gq-"][name$="-variant"]').forEach((input) => {
        input.addEventListener('change', () => {
          this.syncChoiceStyles(input.closest('fieldset'));
          this.syncPrerequisite();
          this.updateNavigation();
        });
      });

      this.root.querySelectorAll('input[name="fe-lkw-funding"], input[name="fe-bus-funding"]').forEach((input) => {
        input.addEventListener('change', () => {
          this.syncChoiceStyles(input.closest('fieldset'));
          this.syncHeavyNote();
          this.updateNavigation();
        });
      });

      this.root.querySelector('[data-fe-prerequisite]')?.addEventListener('change', () => {
        this.clearStatus();
        this.updateNavigation();
      });

      this.root.querySelectorAll('[data-fe-field="callbackDate"], [data-fe-field="callbackTime"]').forEach((input) => {
        input.addEventListener('input', () => this.validateCallbackPreference());
        input.addEventListener('change', () => this.validateCallbackPreference());
      });
      this.field('birthDate')?.addEventListener('input', (event) => {
        event.currentTarget.setCustomValidity('');
        event.currentTarget.removeAttribute('aria-invalid');
      });

      this.root.querySelectorAll('[data-fe-license-answer]').forEach((input) => {
        input.addEventListener('change', () => this.toggleLicenseClasses());
      });

      this.root.querySelectorAll('[data-fe-edit-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const target = Number(button.dataset.feEditStep);
          if (target) this.showStep(target);
        });
      });

      this.bindVideoElements();

      const dialog = this.root.querySelector('[data-fe-cost-dialog]');
      this.root.querySelector('[data-fe-open-costs]')?.addEventListener('click', () => {
        if (!dialog) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
      this.root.querySelector('[data-fe-close-costs]')?.addEventListener('click', () => {
        if (!dialog) return;
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      });
      dialog?.addEventListener('click', (event) => {
        if (event.target === dialog && typeof dialog.close === 'function') dialog.close();
      });

      const specialDialog = this.root.querySelector('[data-fe-special-dialog]');
      this.root.querySelector('[data-fe-close-special]')?.addEventListener('click', () => {
        if (!specialDialog) return;
        if (typeof specialDialog.close === 'function') specialDialog.close();
        else specialDialog.removeAttribute('open');
      });
      specialDialog?.addEventListener('click', (event) => {
        if (event.target === specialDialog && typeof specialDialog.close === 'function') specialDialog.close();
      });

      this.form.addEventListener('submit', (event) => event.preventDefault());
    }

    configureDates() {
      const birthDate = this.field('birthDate');
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayValue = `${year}-${month}-${day}`;
      if (birthDate) birthDate.max = todayValue;
      const callbackDate = this.field('callbackDate');
      if (callbackDate) callbackDate.min = todayValue;
    }

    startFlow(step = 1) {
      this.startScreen.hidden = true;
      this.completeScreen.hidden = true;
      this.flow.hidden = false;
      this.showStep(step, false);
      this.animateVehicleLight(this.selectedCategory() || 'pkw');
    }

    showStep(stepNumber, focusHeading = true) {
      const targetStep = this.steps.find((step) => Number(step.dataset.feStep) === stepNumber);
      if (!targetStep) return;

      this.pauseAllVideos();
      this.clearStatus();
      this.currentStep = stepNumber;

      this.steps.forEach((step) => {
        const isActive = step === targetStep;
        step.hidden = !isActive;
        step.classList.toggle('is-active', isActive);
      });

      if (stepNumber === 2) this.syncVariantPanels();
      if (stepNumber >= 3) this.syncRoutePresentation();
      if (stepNumber === 7) this.renderSummary();
      if (stepNumber === 8) this.updateAgeBranch();

      this.updateProgress();
      this.updateNavigation();
      this.autoplayVideo(targetStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (focusHeading) {
        window.setTimeout(() => {
          const heading = targetStep.querySelector('h2');
          if (!heading) return;
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }, 350);
      }
    }

    updateProgress() {
      if (this.stepLabel) this.stepLabel.textContent = `Schritt ${this.currentStep} von 8`;
      this.dots.forEach((dot) => {
        const step = Number(dot.dataset.step);
        dot.classList.toggle('is-complete', step < this.currentStep);
        dot.classList.toggle('is-current', step === this.currentStep);
      });
    }

    updateNavigation() {
      this.backButton.hidden = false;
      this.nextButton.disabled = false;
      const route = this.currentRoute();

      if (this.currentStep === 1) {
        this.nextButton.textContent = 'Weiter';
      } else if (this.currentStep === 2) {
        this.nextButton.disabled = !route;
        this.nextButton.textContent = 'Weiter';
      } else if (this.currentStep === 7) {
        this.nextButton.textContent = 'Angaben bestätigen';
      } else if (this.currentStep === 8) {
        if (route?.kind === 'contact') this.nextButton.textContent = 'Anfrage absenden';
        else this.nextButton.textContent = this.isMinor || route?.noOnlinePayment ? 'Daten absenden' : 'Zur Zahlung';
      } else {
        this.nextButton.textContent = 'Weiter';
      }
    }

    goBack() {
      if (this.isSubmitting) return;
      if (this.currentStep <= 1) {
        this.pauseAllVideos();
        this.flow.hidden = true;
        this.startScreen.hidden = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.showStep(this.currentStep - 1);
    }

    goForward() {
      if (this.isSubmitting) return;

      if (this.currentStep === 2) {
        const route = this.currentRoute();
        if (!route) {
          this.showRouteStatus('Bitte wähle zuerst eine passende Variante aus.');
          return;
        }
        if (
          route.prerequisite &&
          !['lkw', 'bus'].includes(this.selectedCategory()) &&
          !this.root.querySelector('[data-fe-prerequisite]')?.checked
        ) {
          this.showRouteStatus('Bitte bestätige zuerst die erforderliche Voraussetzung.');
          return;
        }
      }

      if (this.currentStep === 6 && !this.validatePersonalData()) return;
      if (this.currentStep === 7) this.updateAgeBranch();

      if (this.currentStep === 8) {
        this.submitFinal();
        return;
      }

      this.showStep(this.currentStep + 1);
    }

    selectedCategory() {
      return this.form.querySelector('[data-fe-category]:checked')?.dataset.feCategory || '';
    }

    currentRouteKey() {
      const category = this.selectedCategory();

      if (category === 'pkw') {
        const selected = this.form.querySelector('input[name="fe-pkw-route"]:checked')?.value;
        if (!selected) return null;
        const canBeIntensive = ['b_schalter', 'b_automatik', 'b197'].includes(selected);
        const intensive = canBeIntensive && this.root.querySelector('[data-fe-pkw-intensive]')?.checked;
        return intensive ? `${selected}_intensive` : selected;
      }

      if (category === 'motorrad') {
        const motorcycleClass = this.form.querySelector('input[name="fe-motorcycle-class"]:checked')?.value;
        if (!motorcycleClass) return null;
        if (motorcycleClass === 'a' || motorcycleClass === 'a2') {
          const training = this.form.querySelector('input[name="fe-motorcycle-training"]:checked')?.value;
          if (!training) return null;
          const intensive =
            training === 'standard' && this.root.querySelector('[data-fe-motorcycle-intensive]')?.checked;
          return `${motorcycleClass}_${intensive ? 'intensive' : training}`;
        }
        return motorcycleClass;
      }

      if (category === 'lkw') {
        const qualification = this.form.querySelector('input[name="fe-lkw-route"]:checked')?.value;
        if (qualification === 'gq_goods') {
          const variant = this.form.querySelector('input[name="fe-gq-goods-variant"]:checked')?.value;
          return variant ? `gq_goods_${variant}` : null;
        }
        const training = this.root.querySelector('[data-fe-lkw-training]')?.value;
        const license = this.root.querySelector('[data-fe-lkw-license]')?.value;
        if (training === 'conversion') return 'lkw_conversion';
        const routeMap = {
          c: { b: 'lkw_c_b', c1: 'lkw_c_c1' },
          ce: { c: 'lkw_ce_c' },
          cce: { b: 'lkw_cce_b', c1: 'lkw_cce_c1' },
          c1e: { c1: 'lkw_c1e_c1' }
        };
        return routeMap[training]?.[license] || null;
      }

      if (category === 'bus') {
        const qualification = this.form.querySelector('input[name="fe-bus-route"]:checked')?.value;
        if (qualification === 'gq_people') {
          const variant = this.form.querySelector('input[name="fe-gq-people-variant"]:checked')?.value;
          return variant ? `gq_people_${variant}` : null;
        }
        const training = this.root.querySelector('[data-fe-bus-training]')?.value;
        const license = this.root.querySelector('[data-fe-bus-license]')?.value;
        const duration = this.root.querySelector('[data-fe-bus-duration]')?.value;
        if (training === 'de') return license === 'd' ? 'bus_de_d' : null;
        if (training !== 'd' || !['b', 'c1', 'c'].includes(license) || !['long', 'short'].includes(duration)) {
          return null;
        }
        return `bus_d_${license}_${duration}`;
      }

      return null;
    }

    currentRoute() {
      const route = this.routes[this.currentRouteKey()];
      if (!route) return null;
      const category = this.selectedCategory();
      const funding =
        category === 'lkw'
          ? this.form.querySelector('input[name="fe-lkw-funding"]:checked')?.value
          : category === 'bus'
            ? this.form.querySelector('input[name="fe-bus-funding"]:checked')?.value
            : null;
      if ((category === 'lkw' || category === 'bus') && !funding) return null;

      const fundingLabels = {
        self: 'Selbstzahler',
        employer: 'Arbeitgeber übernimmt',
        voucher: 'Bildungsgutschein'
      };
      const bf17 = category === 'pkw' && Boolean(this.root.querySelector('[data-fe-bf17]')?.checked);
      return {
        ...route,
        funding: fundingLabels[funding] || route.funding || '',
        bf17,
        noOnlinePayment: route.noOnlinePayment || bf17
      };
    }

    selectCategory(input) {
      const category = input.dataset.feCategory;
      this.syncChoiceStyles(input.closest('fieldset'));

      this.root.querySelectorAll('[data-fe-vehicle]').forEach((vehicle) => {
        const isActive = vehicle.dataset.feVehicle === category;
        vehicle.classList.toggle('is-active', isActive);
      });
      this.animateVehicleLight(category);

      this.syncVariantPanels();
      this.updateNavigation();
    }

    animateVehicleLight(category) {
      window.clearTimeout(this.vehicleLightTimer);
      this.root.querySelectorAll('[data-fe-vehicle]').forEach((vehicle) => {
        vehicle.classList.remove('is-lit');
      });
      const activeVehicle = this.root.querySelector(`[data-fe-vehicle="${category}"]`);
      if (!activeVehicle) return;
      this.vehicleLightTimer = window.setTimeout(() => {
        if (activeVehicle.classList.contains('is-active')) activeVehicle.classList.add('is-lit');
      }, 650);
    }

    selectMotorcycleClass(input) {
      this.syncChoiceStyles(input.closest('fieldset'));
      const training = this.root.querySelector('[data-fe-motorcycle-training]');
      const stageRequirement = this.root.querySelector('[data-fe-stage-requirement]');
      const needsTraining = input.value === 'a' || input.value === 'a2';

      if (training) training.hidden = !needsTraining;
      if (stageRequirement) {
        stageRequirement.textContent =
          input.value === 'a'
            ? 'A2 seit mindestens 2 Jahren'
            : 'A1 seit mindestens 2 Jahren';
      }
      this.syncMotorcycleOptions();
      this.syncPrerequisite();
      this.updateNavigation();
    }

    syncVariantPanels() {
      const category = this.selectedCategory() || 'pkw';
      const videoGroup = category;

      this.root.querySelectorAll('[data-fe-variant-panel]').forEach((panel) => {
        const isActive = panel.dataset.feVariantPanel === category;
        panel.hidden = !isActive;
      });

      this.root.querySelectorAll('[data-fe-variant-video]').forEach((wrapper) => {
        wrapper.hidden = wrapper.dataset.feVariantVideo !== videoGroup;
      });

      const title = this.root.querySelector('[data-fe-step-two-title]');
      const copy = this.root.querySelector('[data-fe-step-two-copy]');
      if (title) title.textContent = 'Ausbildung wählen';
      if (copy) {
        copy.textContent =
          category === 'lkw' || category === 'bus'
            ? `Wähle die passende ${category === 'lkw' ? 'LKW' : 'Bus'}-Ausbildung und anschließend die Finanzierung.`
            : 'Zusammen finden wir die passende Ausbildung für dich.';
      }

      const routeStatus = this.root.querySelector('[data-fe-route-status]');
      const routeStatusSlot = this.root.querySelector(
        `[data-fe-route-status-slot="${category === 'pkw' ? 'pkw' : 'default'}"]`
      );
      if (routeStatus && routeStatusSlot && routeStatus.parentElement !== routeStatusSlot) {
        routeStatusSlot.appendChild(routeStatus);
      }

      this.syncPkwOptions();
      this.syncMotorcycleOptions();
      this.syncLkwSelection();
      this.syncBusSelection();
      this.syncHeavyOptions();
      this.syncPrerequisite();
      this.syncHeavyNote();
      this.clearStatus();
    }

    syncPkwOptions() {
      const selected = this.form.querySelector('input[name="fe-pkw-route"]:checked')?.value;
      const options = this.root.querySelector('[data-fe-pkw-options]');
      const baseOptions = this.root.querySelector('[data-fe-pkw-base-options]');
      const isBaseB = ['b_schalter', 'b_automatik', 'b197'].includes(selected);
      if (options) options.hidden = false;
      if (baseOptions) baseOptions.hidden = !isBaseB;
      if (!isBaseB) {
        const intensive = this.root.querySelector('[data-fe-pkw-intensive]');
        const bf17 = this.root.querySelector('[data-fe-bf17]');
        if (intensive) intensive.checked = false;
        if (bf17) bf17.checked = false;
      }
    }

    syncMotorcycleOptions() {
      const motorcycleClass = this.form.querySelector('input[name="fe-motorcycle-class"]:checked')?.value;
      const training = this.form.querySelector('input[name="fe-motorcycle-training"]:checked')?.value;
      const row = this.root.querySelector('[data-fe-motorcycle-intensive-row]');
      const intensive = this.root.querySelector('[data-fe-motorcycle-intensive]');
      const canBeIntensive = ['a', 'a2'].includes(motorcycleClass) && training === 'standard';
      if (row) row.hidden = !canBeIntensive;
      if (!canBeIntensive && intensive) intensive.checked = false;
    }

    clearBusTraining() {
      ['[data-fe-bus-training]', '[data-fe-bus-license]', '[data-fe-bus-duration]'].forEach((selector) => {
        const select = this.root.querySelector(selector);
        if (select) select.value = '';
      });
      this.syncBusSelection();
    }

    clearLkwTraining() {
      ['[data-fe-lkw-training]', '[data-fe-lkw-license]'].forEach((selector) => {
        const select = this.root.querySelector(selector);
        if (select) select.value = '';
      });
      this.syncLkwSelection();
    }

    syncLkwSelection() {
      const training = this.root.querySelector('[data-fe-lkw-training]');
      const license = this.root.querySelector('[data-fe-lkw-license]');
      const licenseRow = this.root.querySelector('[data-fe-lkw-license-row]');
      if (!training || !license) return;

      const allowedLicenses = {
        c: ['b', 'c1'],
        ce: ['c'],
        cce: ['b', 'c1'],
        c1e: ['c1']
      };
      const allowed = allowedLicenses[training.value] || [];
      const needsLicense = allowed.length > 0;
      if (licenseRow) licenseRow.hidden = !needsLicense;
      [...license.options].forEach((option) => {
        if (!option.value) return;
        option.hidden = needsLicense && !allowed.includes(option.value);
        option.disabled = !needsLicense || option.hidden;
      });
      if (!needsLicense || license.selectedOptions[0]?.disabled) license.value = '';
    }

    syncBusSelection() {
      const training = this.root.querySelector('[data-fe-bus-training]');
      const license = this.root.querySelector('[data-fe-bus-license]');
      const duration = this.root.querySelector('[data-fe-bus-duration]');
      const licenseRow = this.root.querySelector('[data-fe-bus-license-row]');
      const durationRow = this.root.querySelector('[data-fe-bus-duration-row]');
      if (!training || !license || !duration) return;

      const hasTraining = training.value === 'd' || training.value === 'de';
      if (licenseRow) licenseRow.hidden = false;
      [...license.options].forEach((option) => {
        if (!option.value) return;
        option.hidden =
          training.value === 'de'
            ? option.value !== 'd'
            : training.value === 'd'
              ? option.value === 'd'
              : false;
        option.disabled = option.hidden;
      });
      if (license.selectedOptions[0]?.disabled) license.value = '';

      const needsDuration = training.value === 'd' && ['b', 'c1', 'c'].includes(license.value);
      if (durationRow) durationRow.hidden = !needsDuration;
      if (!needsDuration) duration.value = '';
    }

    syncHeavyOptions() {
      const lkwRoute = this.form.querySelector('input[name="fe-lkw-route"]:checked')?.value;
      const busRoute = this.form.querySelector('input[name="fe-bus-route"]:checked')?.value;
      this.root.querySelectorAll('[data-fe-gq-options]').forEach((fieldset) => {
        const visible =
          (fieldset.dataset.feGqOptions === 'goods' && lkwRoute === 'gq_goods') ||
          (fieldset.dataset.feGqOptions === 'people' && busRoute === 'gq_people');
        fieldset.hidden = !visible;
        if (!visible) {
          fieldset.querySelectorAll('input').forEach((input) => {
            input.checked = false;
          });
          this.syncChoiceStyles(fieldset);
        }
      });
    }

    syncPrerequisite() {
      const routeKey = this.currentRouteKey();
      const route = this.routes[routeKey];
      const row = this.root.querySelector('[data-fe-prerequisite-row]');
      const checkbox = this.root.querySelector('[data-fe-prerequisite]');
      const copy = this.root.querySelector('[data-fe-prerequisite-copy]');
      if (!row || !checkbox || !copy) return;

      const requirement = route?.prerequisite || '';
      const category = this.selectedCategory();
      const structuredHeavyRequirement =
        (category === 'bus' && String(routeKey || '').startsWith('bus_')) ||
        (category === 'lkw' && String(routeKey || '').startsWith('lkw_'));
      const visibleRequirement = structuredHeavyRequirement ? '' : requirement;
      const targetSlot = this.root.querySelector(
        `[data-fe-prerequisite-slot="${
          category === 'pkw' && visibleRequirement
            ? 'pkw'
            : category === 'motorrad' && visibleRequirement
              ? 'motorrad'
              : 'default'
        }"]`
      );
      if (targetSlot && row.parentElement !== targetSlot) targetSlot.appendChild(row);
      if (checkbox.dataset.routeKey !== String(routeKey || '')) checkbox.checked = false;
      checkbox.dataset.routeKey = String(routeKey || '');
      row.hidden = !visibleRequirement;
      checkbox.required = Boolean(visibleRequirement);
      copy.textContent = visibleRequirement;
      if (!visibleRequirement) checkbox.checked = false;
    }

    syncHeavyNote() {
      const note = this.root.querySelector('[data-fe-heavy-note]');
      if (!note) return;
      const category = this.selectedCategory();
      const funding =
        category === 'lkw'
          ? this.form.querySelector('input[name="fe-lkw-funding"]:checked')?.value
          : category === 'bus'
            ? this.form.querySelector('input[name="fe-bus-funding"]:checked')?.value
            : null;
      if (funding === 'voucher') {
        note.textContent =
          'Für Bildungsgutscheine prüfen wir die Fördervoraussetzungen persönlich – einschließlich TQ1 Güterverkehr, TQ2 Servicefahrer und TQ3 Personenbeförderung. Deine Anfrage ist unverbindlich.';
        note.hidden = false;
      } else {
        note.textContent = '';
        note.hidden = true;
      }
    }

    syncRoutePresentation() {
      const route = this.currentRoute();
      if (!route) return;
      const routeKey = this.currentRouteKey();
      this.setVideoSlot('detail', `detail_${routeKey}`);
      this.setVideoSlot(
        'price',
        ['LKW', 'Bus', 'Grundqualifikation'].includes(route.category)
          ? 'price_heavy'
          : this.isMinor || route.noOnlinePayment
            ? 'price_minor'
            : 'price_pkw_moto'
      );
      this.setVideoSlot(
        'checklist',
        route.category === 'Bus'
          ? 'checklist_bus'
          : ['LKW', 'Grundqualifikation'].includes(route.category)
            ? 'checklist_lkw_bkf'
            : 'checklist_pkw_moto'
      );
      this.setVideoSlot(
        'step_6',
        ['LKW', 'Bus', 'Grundqualifikation'].includes(route.category) ? 'step_6_heavy' : 'step_6'
      );

      const title = this.root.querySelector('[data-fe-detail-title]');
      const intro = this.root.querySelector('[data-fe-detail-intro]');
      if (title) title.textContent = route.detailTitle || route.label;
      if (intro) intro.textContent = route.intro || '';

      this.root.querySelectorAll('[data-fe-detail]').forEach((detail) => {
        detail.hidden = detail.dataset.feDetail !== route.detail;
      });

      const professionalCosts = ['LKW', 'Bus', 'Grundqualifikation'].includes(route.category);
      this.root.querySelectorAll('[data-fe-external-costs]').forEach((content) => {
        content.hidden =
          content.dataset.feExternalCosts !== (professionalCosts ? 'professional' : 'standard');
      });

      this.renderPrices(route);
      this.syncChecklist(route);
      this.syncEmployerField(route);
      this.syncRouteFormFields(route);
    }

    renderPrices(route) {
      const pricesBox = this.root.querySelector('[data-fe-prices]');
      const heavyPrice = this.root.querySelector('[data-fe-heavy-price]');
      const dueCard = this.root.querySelector('[data-fe-due-card]');
      if (pricesBox) pricesBox.hidden = false;
      if (heavyPrice) heavyPrice.hidden = route.kind !== 'contact';

      const price = this.config.prices?.[route.price] || {};
      const priceList = this.root.querySelector('[data-fe-price-list]');
      const missing = this.root.querySelector('[data-fe-price-missing]');
      const rows = (price.rows || []).filter((row) => row.isHeading || String(row.value || '').trim());

      if (priceList) {
        priceList.replaceChildren();
        rows.forEach((row) => {
          const wrapper = document.createElement('div');
          if (row.isHeading) {
            wrapper.className = 'fe-price-group-heading';
            wrapper.textContent = row.label;
            priceList.append(wrapper);
            return;
          }
          const term = document.createElement('dt');
          const value = document.createElement('dd');
          if (row.isSpecial) {
            const specialButton = document.createElement('button');
            specialButton.type = 'button';
            specialButton.className = 'fe-special-link';
            specialButton.textContent = row.label;
            specialButton.addEventListener('click', () => {
              const dialog = this.root.querySelector('[data-fe-special-dialog]');
              const copy = this.root.querySelector('[data-fe-special-copy]');
              if (copy) copy.textContent = row.specialText || '';
              if (!dialog) return;
              if (typeof dialog.showModal === 'function') dialog.showModal();
              else dialog.setAttribute('open', '');
            });
            term.append(specialButton);
          } else {
            term.textContent = row.label;
          }

          if (row.promoValue) {
            value.className = 'fe-price-values';
            const oldPrice = document.createElement('s');
            const promoPrice = document.createElement('strong');
            oldPrice.textContent = row.value;
            promoPrice.textContent = row.promoValue;
            value.append(oldPrice, promoPrice);
          } else {
            value.textContent = row.value;
          }
          wrapper.append(term, value);
          if (row.note) {
            const note = document.createElement('small');
            note.textContent = row.note;
            wrapper.append(note);
          }
          priceList.append(wrapper);
        });
      }

      if (missing) missing.hidden = rows.length > 0;

      const total = this.root.querySelector('[data-fe-price-total]');
      const totalValue = this.root.querySelector('[data-fe-price-total-value]');
      const totalNote = this.root.querySelector('[data-fe-price-total-note]');
      if (total) total.hidden = !price.total;
      if (totalValue) totalValue.textContent = price.total || '';
      if (totalNote) totalNote.textContent = price.totalNote || '';

      const due = this.effectiveDuePrice(route);
      if (dueCard) dueCard.hidden = route.kind === 'contact' || !due;
      const duePrice = this.root.querySelector('[data-fe-due-price]');
      if (duePrice) duePrice.textContent = due || '–';
      const dueOldPrice = this.root.querySelector('[data-fe-due-old-price]');
      const promoLabel = this.root.querySelector('[data-fe-promo-label]');
      const hasPromotion = Boolean(String(price.promoBase || '').trim());
      if (dueOldPrice) {
        dueOldPrice.textContent = hasPromotion ? price.base || '' : '';
        dueOldPrice.hidden = !hasPromotion;
      }
      if (promoLabel) {
        promoLabel.textContent = price.promoLabel || 'Aktions-Angebot';
        promoLabel.hidden = !hasPromotion;
      }
      const dueCopy = this.root.querySelector('[data-fe-due-copy]');
      if (dueCopy) {
        dueCopy.textContent = route.payInFull
          ? 'Nur für Volljährige. Dieser Betrag deckt die ausgewählte Schulung vollständig ab.'
          : 'Nur für Volljährige. Der Betrag wird vollständig auf den späteren Ausbildungsvertrag angerechnet.';
      }

      const intro = this.root.querySelector('[data-fe-price-intro]');
      if (intro) {
        intro.textContent =
          route.kind === 'contact'
            ? 'Hier siehst du die voraussichtlichen Fahrschulkosten. Deine Anfrage bleibt unverbindlich und es findet keine Onlinezahlung statt.'
            : 'Hier siehst du alle Fahrschulkosten auf einen Blick. Gebühren von Behörden und Prüfstelle kommen separat hinzu.';
      }
    }

    syncChecklist(route) {
      this.root.querySelectorAll('[data-fe-checklist]').forEach((list) => {
        list.hidden = list.dataset.feChecklist !== route.checklist;
      });
    }

    syncEmployerField(route) {
      const wrapper = this.root.querySelector('[data-fe-employer-fields]');
      const fields = ['employer', 'companyContact', 'employerPhone', 'participants', 'period']
        .map((name) => this.field(name))
        .filter(Boolean);
      const requiredFields = new Set(['employer', 'participants', 'period']);
      const isEmployer = route.funding === 'Arbeitgeber übernimmt';
      if (wrapper) wrapper.hidden = !isEmployer;
      fields.forEach((input) => {
        input.required = isEmployer && requiredFields.has(input.dataset.feField);
        if (!isEmployer) {
          input.value = input.dataset.feField === 'participants' ? '1' : '';
        } else if (input.dataset.feField === 'participants' && !input.value) {
          input.value = '1';
        }
      });
    }

    syncRouteFormFields(route) {
      const professional = ['LKW', 'Bus', 'Grundqualifikation'].includes(route.category);
      const selfPayingHeavy =
        ['LKW', 'Bus'].includes(route.category) && route.funding === 'Selbstzahler';
      const personalCopy = this.root.querySelector('[data-fe-personal-copy]');
      if (personalCopy) {
        personalCopy.textContent = selfPayingHeavy
          ? 'Für deine unverbindliche Anfrage benötigen wir neben deinen Kontaktdaten auch deine private Anschrift.'
          : professional
            ? 'Für deine unverbindliche Anfrage benötigen wir nur die Angaben, mit denen wir dich erreichen und passend beraten können.'
            : 'Damit wir alles für dich vorbereiten können, brauchen wir noch ein paar Angaben. Bitte fülle alle Felder aus.';
      }

      const privateFieldNames = new Set(['birthPlace', 'street', 'zip', 'city', 'branch']);
      const addressFieldNames = new Set(['street', 'zip', 'city']);
      this.root.querySelectorAll('[data-fe-private-only]').forEach((wrapper) => {
        const input = wrapper.querySelector('[data-fe-field]');
        if (!input) return;
        const showField =
          !professional || (selfPayingHeavy && addressFieldNames.has(input.dataset.feField));
        wrapper.hidden = !showField;
        input.required = showField && privateFieldNames.has(input.dataset.feField);
        if (!showField) input.value = '';
      });

      const callbackWrapper = this.root.querySelector('[data-fe-callback-fields]');
      const callbackFields = ['callbackDate', 'callbackTime'].map((name) => this.field(name)).filter(Boolean);
      if (callbackWrapper) callbackWrapper.hidden = !professional;
      callbackFields.forEach((input) => {
        input.required = professional;
        if (!professional) {
          input.value = '';
          input.setCustomValidity('');
        }
      });
      if (professional) this.validateCallbackPreference();

      const foreignWrapper = this.root.querySelector('[data-fe-foreign-fields]');
      const foreignFields = ['foreignCountry', 'foreignClasses'].map((name) => this.field(name)).filter(Boolean);
      if (foreignWrapper) foreignWrapper.hidden = !route.foreignLicense;
      foreignFields.forEach((input) => {
        input.required = Boolean(route.foreignLicense);
        if (!route.foreignLicense) input.value = '';
      });

      const licenseQuestion = this.root.querySelector('[data-fe-license-question]');
      const licenseClasses = this.root.querySelector('[data-fe-license-classes]');
      const skipGeneralLicenseQuestion = Boolean(route.prerequisite || route.foreignLicense);
      if (licenseQuestion) licenseQuestion.hidden = skipGeneralLicenseQuestion;
      if (skipGeneralLicenseQuestion) {
        this.root.querySelectorAll('[data-fe-license-answer]').forEach((input) => {
          input.checked = false;
          input.required = false;
        });
        if (licenseClasses) {
          licenseClasses.hidden = true;
          licenseClasses.querySelectorAll('[data-fe-license-class]').forEach((input) => {
            input.checked = false;
          });
        }
      } else {
        this.root.querySelectorAll('[data-fe-license-answer]').forEach((input) => {
          input.required = true;
        });
        this.toggleLicenseClasses();
      }
    }

    effectiveDuePrice(route) {
      const price = this.config.prices?.[route?.price] || {};
      const product = this.config.products?.[route?.product] || {};
      if (route?.payInFull) return String(price.total || product.price || price.base || '').trim();
      return String(price.promoBase || price.base || product.price || '').trim();
    }

    comparablePrice(value) {
      const normalized = String(value || '')
        .replace(/\s/g, '')
        .replace(/[^\d,.-]/g, '')
        .replace(/\.(?=\d{3}(?:\D|$))/g, '')
        .replace(',', '.');
      const number = Number.parseFloat(normalized);
      return Number.isFinite(number) ? number : null;
    }

    syncChoiceStyles(container) {
      if (!container) return;
      container.querySelectorAll('.fe-choice').forEach((choice) => {
        choice.classList.toggle('is-selected', Boolean(choice.querySelector('input:checked')));
      });
    }

    showRouteStatus(message) {
      const status = this.root.querySelector('[data-fe-route-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = false;
    }

    clearStatus() {
      this.root.querySelectorAll('[data-fe-route-status], [data-fe-submit-status]').forEach((status) => {
        status.textContent = '';
        status.hidden = true;
      });
    }

    toggleLicenseClasses() {
      const answer = this.form.querySelector('[data-fe-license-answer]:checked')?.value;
      const classFieldset = this.root.querySelector('[data-fe-license-classes]');
      if (!classFieldset) return;
      classFieldset.hidden = answer !== 'Ja';
      if (answer !== 'Ja') {
        classFieldset.querySelectorAll('[data-fe-license-class]').forEach((checkbox) => {
          checkbox.checked = false;
        });
        this.root.querySelector('[data-fe-license-error]')?.setAttribute('hidden', '');
      }
    }

    validatePersonalData() {
      const step = this.steps.find((item) => Number(item.dataset.feStep) === 6);
      this.validateCallbackPreference();
      const requiredFields = [...step.querySelectorAll('input[required], select[required]')].filter(
        (field) => !field.closest('[hidden]')
      );
      let firstInvalid = null;

      requiredFields.forEach((field) => {
        const valid = field.checkValidity();
        field.setAttribute('aria-invalid', String(!valid));
        if (!valid && !firstInvalid) firstInvalid = field;
      });

      const licenseAnswer = this.form.querySelector('[data-fe-license-answer]:checked')?.value;
      let licenseClassesValid = true;
      if (licenseAnswer === 'Ja') {
        licenseClassesValid = Boolean(this.form.querySelector('[data-fe-license-class]:checked'));
      }

      const licenseError = this.root.querySelector('[data-fe-license-error]');
      if (licenseError) licenseError.hidden = licenseClassesValid;

      const status = this.root.querySelector('[data-fe-form-status]');
      if (firstInvalid || !licenseClassesValid) {
        if (status) {
          status.textContent = 'Bitte prüfe die markierten Pflichtfelder.';
          status.hidden = false;
        }
        (firstInvalid || this.root.querySelector('[data-fe-license-classes]'))?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        firstInvalid?.focus({ preventScroll: true });
        return false;
      }

      const ageMessage = this.agePlausibilityMessage();
      const birthDate = this.field('birthDate');
      if (birthDate) {
        birthDate.setCustomValidity(ageMessage || '');
        birthDate.setAttribute('aria-invalid', String(Boolean(ageMessage)));
      }
      if (ageMessage) {
        if (status) {
          status.textContent = ageMessage;
          status.hidden = false;
        }
        birthDate?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        birthDate?.focus({ preventScroll: true });
        return false;
      }

      if (status) status.hidden = true;
      this.updateAgeBranch();
      return true;
    }

    validateCallbackPreference() {
      const route = this.currentRoute();
      const professional = ['LKW', 'Bus', 'Grundqualifikation'].includes(route?.category);
      const callbackDate = this.field('callbackDate');
      const callbackTime = this.field('callbackTime');
      if (!callbackDate || !callbackTime) return true;

      callbackDate.setCustomValidity('');
      callbackTime.setCustomValidity('');
      if (!professional || !callbackDate.value || !callbackTime.value) return true;

      const selectedDate = new Date(`${callbackDate.value}T12:00:00`);
      const weekday = selectedDate.getDay();
      if (weekday === 0 || weekday === 6) {
        callbackDate.setCustomValidity('Bitte wähle einen Werktag von Montag bis Freitag.');
        return false;
      }

      const selectedDateTime = new Date(`${callbackDate.value}T${callbackTime.value}:00`);
      if (selectedDateTime.getTime() <= Date.now()) {
        callbackTime.setCustomValidity('Bitte wähle einen zukünftigen Rückrufzeitpunkt.');
        return false;
      }
      return true;
    }

    calculateAge(dateString) {
      if (!dateString) return null;
      const birthDate = new Date(`${dateString}T12:00:00`);
      if (Number.isNaN(birthDate.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();
      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
      }
      return age;
    }

    canBeginBeforeMinimumAge(minimumAge, leadMonths = 6) {
      const dateString = this.value('birthDate');
      if (!dateString) return false;
      const birthDate = new Date(`${dateString}T12:00:00`);
      if (Number.isNaN(birthDate.getTime())) return false;
      const earliestStart = new Date(
        birthDate.getFullYear() + minimumAge,
        birthDate.getMonth() - leadMonths,
        birthDate.getDate(),
        12
      );
      return Date.now() >= earliestStart.getTime();
    }

    agePlausibilityMessage() {
      const route = this.currentRoute();
      const routeKey = this.currentRouteKey();
      const age = this.calculateAge(this.value('birthDate'));
      if (!route || age === null) return '';

      const professional = ['LKW', 'Bus', 'Grundqualifikation'].includes(route.category);
      if (professional && age < 18) {
        return 'LKW-, Bus- und Grundqualifikationsanfragen können über diese Voranmeldung erst ab 18 Jahren gestellt werden. Melde dich bitte gemeinsam mit einer erziehungsberechtigten Person direkt bei uns.';
      }
      if (routeKey === 'b196' && age < 25) {
        return 'B196 ist erst ab 25 Jahren möglich. Bitte wähle eine andere Motorradklasse oder melde dich für eine persönliche Beratung bei uns.';
      }
      if (route.bf17 && (!this.canBeginBeforeMinimumAge(17) || age >= 18)) {
        return 'BF17 passt zu deiner Altersangabe nicht. Die Ausbildung kann frühestens sechs Monate vor dem 17. Geburtstag beginnen und ist für Minderjährige vorgesehen.';
      }

      const minimumAges = {
        a_standard: 24,
        a_intensive: 24,
        a2_standard: 18,
        a2_intensive: 18,
        a1: 16,
        am: 15,
        mofa: 15
      };
      const minimumAge = minimumAges[routeKey];
      if (minimumAge && !this.canBeginBeforeMinimumAge(minimumAge)) {
        return `Für ${route.label} kannst du die Ausbildung frühestens sechs Monate vor deinem ${minimumAge}. Geburtstag beginnen.`;
      }
      return '';
    }

    updateAgeBranch() {
      const route = this.currentRoute();
      const age = this.calculateAge(this.value('birthDate'));
      this.isMinor = age !== null && age < 18;
      const isContact = route?.kind === 'contact';
      const isNoPaymentApplication = !isContact && (this.isMinor || route?.noOnlinePayment);

      const adultFinal = this.root.querySelector('[data-fe-adult-final]');
      const minorFinal = this.root.querySelector('[data-fe-minor-final]');
      const contactFinal = this.root.querySelector('[data-fe-contact-final]');
      const finalIntro = this.root.querySelector('[data-fe-final-intro]');
      const due = this.root.querySelector('[data-fe-summary-due]');
      const price = this.config.prices?.[route?.price] || {};

      this.setVideoSlot(
        'step_8',
        isContact ? 'step_8_contact' : isNoPaymentApplication ? 'step_8_minor' : 'step_8'
      );

      if (adultFinal) adultFinal.hidden = isContact || isNoPaymentApplication;
      if (minorFinal) minorFinal.hidden = isContact || !isNoPaymentApplication;
      if (contactFinal) contactFinal.hidden = !isContact;

      if (due) {
        due.textContent = isContact
          ? 'Nach persönlicher Beratung'
          : isNoPaymentApplication
            ? 'Bei Vertragsabschluss vor Ort'
            : 'Heute online';
      }

      if (finalIntro) {
        finalIntro.textContent = isContact
          ? 'Bitte prüfe die folgenden Hinweise. Deine Anfrage wird anschließend unverbindlich an uns gesendet.'
          : isNoPaymentApplication
            ? 'Bitte prüfe die folgenden Hinweise. Deine Daten werden anschließend ohne Onlinezahlung an uns gesendet.'
            : 'Bitte prüfe die folgenden Hinweise. Danach geht es zur sicheren Zahlung.';
      }

      const finalPrice = this.root.querySelector('[data-fe-final-price]');
      const minorPrice = this.root.querySelector('[data-fe-minor-price]');
      const finalPriceTitle = this.root.querySelector('[data-fe-final-price-title]');
      const finalPaymentCopy = this.root.querySelector('[data-fe-final-payment-copy]');
      const duePrice = this.effectiveDuePrice(route);
      if (finalPrice) finalPrice.textContent = duePrice || 'noch nicht hinterlegt';
      if (minorPrice) minorPrice.textContent = duePrice || 'wird vor Ort geklärt';
      if (finalPriceTitle) finalPriceTitle.textContent = route?.payInFull ? 'Heute vollständig fällig' : 'Heute fällig';
      if (finalPaymentCopy) {
        finalPaymentCopy.textContent = route?.payInFull
          ? 'Mit der Onlinezahlung wird die ausgewählte Schulung vollständig bezahlt. Externe Gebühren sind nicht enthalten.'
          : 'Die Zahlung gilt als Vorauszahlung und wird beim späteren Abschluss deines Ausbildungsvertrags vollständig auf den Grundbetrag angerechnet. Alle weiteren Kosten werden erst im Laufe deiner Ausbildung fällig.';
      }
      this.updateNavigation();
    }

    renderSummary() {
      const route = this.currentRoute();
      if (!route) return;
      this.updateAgeBranch();

      const routeValue = this.root.querySelector('[data-fe-summary-route]');
      const categoryValue = this.root.querySelector('[data-fe-summary-category]');
      const fundingRow = this.root.querySelector('[data-fe-summary-funding-row]');
      const fundingValue = this.root.querySelector('[data-fe-summary-funding]');
      if (routeValue) routeValue.textContent = `${route.label}${route.bf17 ? ' + BF17' : ''}`;
      if (categoryValue) categoryValue.textContent = route.category;
      if (fundingRow) fundingRow.hidden = !route.funding;
      if (fundingValue) fundingValue.textContent = route.funding || '';

      const price = this.config.prices?.[route.price] || {};
      const priceValue = this.root.querySelector('[data-fe-summary-price]');
      const priceLabel = this.root.querySelector('[data-fe-summary-price-label]');
      const paymentTitle = this.root.querySelector('[data-fe-summary-payment-title]');
      if (route.kind === 'contact') {
        if (priceLabel) priceLabel.textContent = 'Voraussichtliche Fahrschulkosten';
        if (priceValue) priceValue.textContent = price.total || 'werden persönlich geklärt';
        if (paymentTitle) paymentTitle.textContent = 'Nächster Schritt';
      } else {
        if (priceLabel) priceLabel.textContent = route.payInFull ? 'Heute fällig' : 'Grundbetrag';
        if (priceValue) priceValue.textContent = this.effectiveDuePrice(route) || 'noch nicht hinterlegt';
        if (paymentTitle) paymentTitle.textContent = 'Zahlung';
      }

      const summary = this.root.querySelector('[data-fe-summary-personal]');
      if (!summary) return;
      summary.replaceChildren();

      const licenseAnswer = this.form.querySelector('[data-fe-license-answer]:checked')?.value || '–';
      const licenseClasses = [...this.form.querySelectorAll('[data-fe-license-class]:checked')]
        .map((input) => input.value)
        .join(', ');
      const professional = ['LKW', 'Bus', 'Grundqualifikation'].includes(route.category);
      const selfPayingHeavy =
        ['LKW', 'Bus'].includes(route.category) && route.funding === 'Selbstzahler';

      const rows = [
        ['Name', `${this.value('firstName')} ${this.value('lastName')}`.trim()],
        ['Geburtsdatum', this.formatDate(this.value('birthDate'))]
      ];
      if (!professional) rows.push(['Geburtsort', this.value('birthPlace')]);
      if (!professional || selfPayingHeavy) {
        rows.push(['Adresse', `${this.value('street')}, ${this.value('zip')} ${this.value('city')}`]);
      }
      rows.push(['E-Mail', this.value('email')], ['Telefon', this.value('phone')]);
      if (!professional) rows.push(['Filiale', this.value('branch')]);

      if (route.prerequisite) rows.push(['Bestätigte Voraussetzung', route.prerequisite.replace(/^Ja,\s*/i, '')]);
      else if (route.foreignLicense) {
        rows.push(['Ausstellungsland', this.value('foreignCountry')]);
        rows.push(['Ausländische Klassen', this.value('foreignClasses')]);
      } else {
        rows.push(['Führerschein vorhanden', licenseAnswer]);
        rows.push(['Vorhandene Klassen', licenseAnswer === 'Ja' ? licenseClasses : 'Keine']);
      }

      if (route.funding === 'Arbeitgeber übernimmt') {
        rows.push(['Arbeitgeber', this.value('employer')]);
        rows.push(['Ansprechperson im Unternehmen', this.value('companyContact')]);
        if (this.value('employerPhone')) rows.push(['Telefonnummer des Arbeitgebers', this.value('employerPhone')]);
        rows.push(['Teilnehmende', this.value('participants')]);
        rows.push(['Gewünschter Zeitraum', this.value('period')]);
      }
      if (route.kind === 'contact') {
        rows.push(['Gewünschter Rückruf', `${this.formatDate(this.value('callbackDate'))}, ${this.value('callbackTime')} Uhr`]);
      }
      if (this.value('notes')) rows.push(['Hinweise', this.value('notes')]);

      rows.forEach(([label, value]) => {
        const row = document.createElement('div');
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = label;
        description.textContent = value || '–';
        row.append(term, description);
        summary.append(row);
      });
    }

    formatDate(dateString) {
      if (!dateString) return '–';
      const [year, month, day] = dateString.split('-');
      return year && month && day ? `${day}.${month}.${year}` : dateString;
    }

    field(name) {
      return this.form.querySelector(`[data-fe-field="${name}"]`);
    }

    value(name) {
      return this.field(name)?.value.trim() || '';
    }

    selectedLicenseClasses() {
      return [...this.form.querySelectorAll('[data-fe-license-class]:checked')].map(
        (input) => input.value
      );
    }

    selectedRouteLicenseClasses() {
      const routeKey = this.currentRouteKey() || '';
      const routeClassMap = {
        lkw_c_b: ['B'],
        lkw_c_c1: ['C1'],
        lkw_ce_c: ['C'],
        lkw_cce_b: ['B'],
        lkw_cce_c1: ['C1'],
        lkw_c1e_c1: ['C1'],
        bus_d_b_long: ['B'],
        bus_d_b_short: ['B'],
        bus_d_c1_long: ['C1'],
        bus_d_c1_short: ['C1'],
        bus_d_c_long: ['C'],
        bus_d_c_short: ['C'],
        bus_de_d: ['D']
      };
      return routeClassMap[routeKey] || this.selectedLicenseClasses();
    }

    consentSnapshot() {
      const acceptedAt = new Date().toISOString();
      const accepted = [...this.root.querySelectorAll('[data-fe-consent]:checked')].map((input) => ({
        key: input.dataset.feConsent,
        text: input.closest('label')?.textContent?.replace(/\s+/g, ' ').trim() || ''
      }));
      return {
        acceptedAt,
        textVersion: '2026-07-31-v1',
        accepted
      };
    }

    submissionData(status) {
      const route = this.currentRoute();
      const routeKey = this.currentRouteKey() || '';
      const licenseAnswer = this.form.querySelector('[data-fe-license-answer]:checked')?.value || 'Nein';
      const routeLicenseClasses = this.selectedRouteLicenseClasses();
      const noOnlinePayment = route?.kind === 'contact' || this.isMinor || route?.noOnlinePayment;
      const data = {
        schemaVersion: 3,
        eventType: 'submission.created',
        submissionId: this.submissionId,
        revision: 1,
        status,
        createdAt: new Date().toISOString(),
        source: 'Shopify Vor-Anmeldung',
        origin: window.location.origin,
        clientElapsedMs: Math.max(0, Date.now() - this.startedAt),
        honeypot: this.root.querySelector('[data-fe-honeypot]')?.value || '',
        routeGroup: route?.recipient === 'professional' ? 'professional' : 'standard',
        customer: {
          firstName: this.value('firstName'),
          lastName: this.value('lastName'),
          birthDate: this.value('birthDate'),
          birthPlace: this.value('birthPlace'),
          address: {
            street: this.value('street'),
            postalCode: this.value('zip'),
            city: this.value('city')
          },
          email: this.value('email'),
          phone: this.value('phone'),
          minor: this.isMinor
        },
        selection: {
          routeKey,
          label: `${route?.label || 'Vor-Anmeldung'}${route?.bf17 ? ' + BF17' : ''}`,
          category: route?.category || '',
          branch: this.value('branch'),
          funding: route?.funding || 'Selbstzahler',
          bf17: Boolean(route?.bf17),
          intensive: /_intensive$/.test(routeKey),
          payInFull: Boolean(route?.payInFull),
          prerequisite: route?.prerequisite || '',
          prerequisiteConfirmed: route?.prerequisite
            ? ['lkw', 'bus'].includes(this.selectedCategory()) ||
              Boolean(this.root.querySelector('[data-fe-prerequisite]')?.checked)
            : null,
          license: {
            hasLicense: route?.prerequisite ? true : licenseAnswer === 'Ja',
            classes: route?.prerequisite || licenseAnswer === 'Ja' ? routeLicenseClasses : [],
            foreignCountry: this.value('foreignCountry'),
            foreignClasses: this.value('foreignClasses')
          },
          employer: {
            company: this.value('employer'),
            contact: this.value('companyContact'),
            phone: this.value('employerPhone'),
            participants: this.value('participants'),
            period: this.value('period')
          },
          callback: {
            date: this.value('callbackDate'),
            time: this.value('callbackTime')
          },
          notes: this.value('notes'),
          transmissionType: noOnlinePayment
            ? 'Anfrage ohne Onlinezahlung'
            : 'Vor-Anmeldung mit anschließendem Shopify-Checkout'
        },
        payment: {
          onlinePaymentRequired: !noOnlinePayment,
          mode: noOnlinePayment ? 'none' : route?.payInFull ? 'full' : 'deposit',
          status: noOnlinePayment ? 'not_required' : 'pending'
        },
        priceSnapshot: this.priceSnapshot(route),
        consent: this.consentSnapshot()
      };
      data.officeMail = this.buildOfficeMail(data);
      return data;
    }

    moneySnapshot(value) {
      const display = String(value || '').trim();
      const amount = this.comparablePrice(display);
      return {
        display,
        cents: amount === null ? null : Math.round(amount * 100)
      };
    }

    priceSnapshot(route) {
      const price = this.config.prices?.[route?.price] || {};
      const product = this.config.products?.[route?.product] || {};
      return {
        capturedAt: new Date().toISOString(),
        currency: this.config.currency || 'EUR',
        profileKey: route?.price || '',
        productKey: route?.product || '',
        regularBase: this.moneySnapshot(price.base),
        promotionalBase: String(price.promoBase || '').trim()
          ? {
              ...this.moneySnapshot(price.promoBase),
              label: String(price.promoLabel || 'Aktions-Angebot').trim()
            }
          : null,
        due: this.moneySnapshot(this.effectiveDuePrice(route)),
        estimatedTotal: this.moneySnapshot(price.total),
        totalNote: String(price.totalNote || '').trim(),
        rows: (price.rows || [])
          .filter((row) => !row.isHeading && String(row.value || row.promoValue || '').trim())
          .map((row) => ({
            role: String(row.role || '').trim(),
            label: String(row.label || '').trim(),
            regular: this.moneySnapshot(row.value),
            promotional: String(row.promoValue || '').trim()
              ? this.moneySnapshot(row.promoValue)
              : null,
            note: String(row.note || row.specialText || '').trim()
          })),
        shopify: {
          productId: Number(product.productId || 0) || null,
          productHandle: String(product.productHandle || '').trim(),
          productTitle: String(product.productTitle || '').trim(),
          variantId: Number(product.variantId || 0) || null,
          variantTitle: String(product.variantTitle || '').trim(),
          price: this.moneySnapshot(product.price)
        }
      };
    }

    buildOfficeMail(data) {
      const customer = data.customer;
      const selection = data.selection;
      const price = data.priceSnapshot;
      const licenseClasses = selection.license.classes.join(', ') || 'Keine';
      const statusLabel = data.status === 'checkout_gestartet'
        ? 'Vor-Anmeldung – Checkout wird gestartet'
        : selection.transmissionType;
      const priceRows = price.rows.map((row) => {
        const value = row.promotional?.display
          ? `${row.regular.display} → ${row.promotional.display}`
          : row.regular.display;
        return `- ${row.label}: ${value}${row.note ? ` (${row.note})` : ''}`;
      });
      const acceptedConsents = data.consent.accepted.map((item) => `- ${item.text}`);
      const subject = `[Vor-Anmeldung] ${selection.label} – ${customer.firstName} ${customer.lastName}`;
      const text = [
        'NEUER VORGANG',
        `Status: ${statusLabel}`,
        `Vorgangs-ID: ${data.submissionId}`,
        `Eingang: ${data.createdAt}`,
        `Empfängergruppe: ${data.routeGroup}`,
        '',
        'AUSBILDUNG',
        `Bereich: ${selection.category}`,
        `Ausbildung: ${selection.label}`,
        `Technischer Weg: ${selection.routeKey}`,
        `Filiale: ${selection.branch || '–'}`,
        `Finanzierung: ${selection.funding}`,
        `BF17: ${selection.bf17 ? 'Ja' : 'Nein'}`,
        `Intensivkurs: ${selection.intensive ? 'Ja' : 'Nein'}`,
        selection.prerequisite ? `Voraussetzung: ${selection.prerequisite}` : null,
        `Führerschein vorhanden: ${selection.license.hasLicense ? 'Ja' : 'Nein'}`,
        `Vorhandene Klassen: ${licenseClasses}`,
        selection.license.foreignCountry ? `Ausstellungsland: ${selection.license.foreignCountry}` : null,
        selection.license.foreignClasses ? `Ausländische Klassen: ${selection.license.foreignClasses}` : null,
        '',
        'KUNDENDATEN',
        `Name: ${customer.firstName} ${customer.lastName}`,
        `Geburtsdatum: ${this.formatDate(customer.birthDate)}`,
        customer.birthPlace ? `Geburtsort: ${customer.birthPlace}` : null,
        customer.address.street
          ? `Anschrift: ${customer.address.street}, ${customer.address.postalCode} ${customer.address.city}`
          : null,
        `E-Mail: ${customer.email}`,
        `Telefon: ${customer.phone}`,
        `Minderjährig: ${customer.minor ? 'Ja' : 'Nein'}`,
        '',
        selection.employer.company ? 'ARBEITGEBER' : null,
        selection.employer.company ? `Unternehmen: ${selection.employer.company}` : null,
        selection.employer.contact ? `Ansprechperson: ${selection.employer.contact}` : null,
        selection.employer.phone ? `Telefon: ${selection.employer.phone}` : null,
        selection.employer.participants ? `Teilnehmende: ${selection.employer.participants}` : null,
        selection.employer.period ? `Zeitraum: ${selection.employer.period}` : null,
        selection.employer.company ? '' : null,
        selection.callback.date && selection.callback.time ? 'RÜCKRUF' : null,
        selection.callback.date && selection.callback.time
          ? `${this.formatDate(selection.callback.date)}, ${selection.callback.time} Uhr`
          : null,
        selection.callback.date && selection.callback.time ? '' : null,
        'PREISNACHWEIS',
        `Preisstand: ${price.capturedAt}`,
        `Preisprofil: ${price.profileKey}`,
        `Regulärer Grundbetrag: ${price.regularBase.display || '–'}`,
        price.promotionalBase
          ? `${price.promotionalBase.label}: ${price.promotionalBase.display}`
          : 'Aktionspreis: keiner hinterlegt',
        `Heute fällig: ${price.due.display || '–'}`,
        `Voraussichtliche Fahrschulkosten: ${price.estimatedTotal.display || '–'}`,
        price.totalNote ? `Berechnungsgrundlage: ${price.totalNote}` : null,
        ...priceRows,
        price.shopify.variantId ? `Shopify-Varianten-ID: ${price.shopify.variantId}` : null,
        price.shopify.price.display ? `Shopify-Produktpreis: ${price.shopify.price.display}` : null,
        '',
        'ZAHLUNG',
        `Onlinezahlung erforderlich: ${data.payment.onlinePaymentRequired ? 'Ja' : 'Nein'}`,
        `Zahlungsart: ${data.payment.mode}`,
        `Zahlungsstatus: ${data.payment.status}`,
        '',
        'BESTÄTIGUNGEN',
        `Textversion: ${data.consent.textVersion}`,
        `Bestätigt am: ${data.consent.acceptedAt}`,
        ...acceptedConsents,
        selection.notes ? '' : null,
        selection.notes ? 'HINWEISE' : null,
        selection.notes || null
      ].filter((line) => line !== null && line !== undefined).join('\n');
      return { subject, text };
    }

    lineItemProperties(data) {
      return {
        '_Vorgangs-ID': data.submissionId
      };
    }

    lineItemSubmissionId(properties) {
      return String(
        properties?.['_Vorgangs-ID'] ||
        properties?.['Vorgangs-ID'] ||
        ''
      );
    }

    requiredConsentsAccepted() {
      const route = this.currentRoute();
      const names = route?.kind === 'contact'
        ? ['accuracyContact', 'contactTerms']
        : this.isMinor || route?.noOnlinePayment
          ? ['accuracyMinor', 'minorTerms']
          : ['accuracy', 'adultTerms'];
      return names.every((name) => this.root.querySelector(`[data-fe-consent="${name}"]`)?.checked);
    }

    async submitFinal() {
      const status = this.root.querySelector('[data-fe-submit-status]');
      if (this.root.querySelector('[data-fe-honeypot]')?.value) return;
      const ageMessage = this.agePlausibilityMessage();
      if (ageMessage) {
        if (status) {
          status.textContent = ageMessage;
          status.hidden = false;
        }
        return;
      }
      if (!this.requiredConsentsAccepted()) {
        if (status) {
          status.textContent = 'Bitte bestätige beide Pflichtangaben, bevor du fortfährst.';
          status.hidden = false;
        }
        return;
      }

      if (status) status.hidden = true;
      const route = this.currentRoute();
      const data = this.submissionData(
        route?.kind === 'contact' || this.isMinor || route?.noOnlinePayment
          ? 'anfrage_eingegangen'
          : 'checkout_gestartet'
      );

      if (route?.kind === 'contact' || this.isMinor || route?.noOnlinePayment) {
        this.isSubmitting = true;
        this.setSubmittingState('Daten werden gesendet …');
        try {
          await this.sendToMake(data);
          this.showCompletion({
            kind: route?.kind === 'contact' ? 'contact' : route?.bf17 ? 'bf17' : 'minor',
            route: data.selection.label,
            branch: data.selection.branch,
            base: this.effectiveDuePrice(route)
          });
          this.resetSubmissionId();
        } catch (error) {
          this.isSubmitting = false;
          this.setSubmittingState(null);
          if (status) {
            status.textContent =
              error?.message ||
              'Die Daten konnten gerade nicht sicher übermittelt werden. Bitte versuche es noch einmal.';
            status.hidden = false;
          }
        }
        return;
      }

      this.startCheckout(data);
    }

    async startCheckout(data) {
      const status = this.root.querySelector('[data-fe-submit-status]');
      const route = this.currentRoute();
      const product = this.config.products?.[route?.product] || {};
      const price = this.config.prices?.[route?.price] || {};
      const variantId = Number(product.variantId);
      const productReady = product.ready === true && Number.isFinite(variantId) && variantId > 0;

      if (!productReady) {
        if (status) {
          status.textContent = this.setupMessage(
            `Der Grundbetrag für ${route?.label || 'diese Ausbildung'} ist noch nicht mit einem verfügbaren Shopify-Produkt verknüpft. Bitte wähle das Produkt im Theme-Editor aus.`,
            'Die Onlinezahlung kann für diese Ausbildung gerade nicht vorbereitet werden. Bitte melde dich kurz bei uns.'
          );
          status.hidden = false;
        }
        return;
      }

      const expectedPrice = this.comparablePrice(this.effectiveDuePrice(route));
      const checkoutPrice = this.comparablePrice(product.price);
      if (
        expectedPrice !== null &&
        checkoutPrice !== null &&
        Math.abs(expectedPrice - checkoutPrice) > 0.001
      ) {
        if (status) {
          const expectedLabel = price.promoBase ? 'Aktionspreis' : 'heute fällige Betrag';
          status.textContent = this.setupMessage(
            `Der eingetragene ${expectedLabel} (${this.effectiveDuePrice(route)}) stimmt noch nicht mit dem Preis des verknüpften Shopify-Produkts (${product.price}) überein. Bitte gleiche beide Preise im Theme-Editor beziehungsweise Produkt ab.`,
            'Die Onlinezahlung kann gerade nicht sicher vorbereitet werden. Bitte melde dich kurz bei uns.'
          );
          status.hidden = false;
        }
        return;
      }

      this.isSubmitting = true;
      this.setSubmittingState('Daten werden gesendet …');

      try {
        this.setSubmittingState('Checkout wird vorbereitet …');
        const shopRoot = window.Shopify?.routes?.root || '/';
        const checkoutUrl = this.buildCheckoutPermalink(shopRoot, variantId, data);

        this.setSubmittingState('Daten werden gesendet …');
        await this.sendToMake(data);
        this.storeSubmitState({
          kind: 'checkout',
          submissionId: data.submissionId,
          route: data.selection.label
        });
        // Checkout und Make behalten die bisherige Vorgangs-ID. Für einen
        // späteren neuen Vorgang im selben Tab wird sofort eine neue ID
        // bereitgestellt – auch bei einer Rückkehr aus dem Browser-Cache.
        this.resetSubmissionId();
        this.setSubmittingState('Checkout wird geöffnet …');
        window.location.assign(checkoutUrl);
      } catch (error) {
        this.isSubmitting = false;
        this.setSubmittingState(null);
        if (status) {
          status.textContent = error?.message || 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.';
          status.hidden = false;
        }
      }
    }

    base64UrlEncode(value) {
      const bytes = new TextEncoder().encode(String(value));
      let binary = '';
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      return window.btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
    }

    buildCheckoutPermalink(shopRoot, variantId, data) {
      const normalizedRoot = String(shopRoot || '/').endsWith('/')
        ? String(shopRoot || '/')
        : `${shopRoot}/`;
      const checkoutUrl = new URL(
        `${normalizedRoot}cart/${variantId}:1`,
        window.location.origin
      );
      const properties = this.base64UrlEncode(
        JSON.stringify(this.lineItemProperties(data))
      );
      checkoutUrl.searchParams.set('properties', properties);
      return checkoutUrl.toString();
    }

    async sendToMake(data) {
      const makeStatusKey = `${data.submissionId}:${data.status}`;
      if (this.sentMakeStatuses.has(makeStatusKey)) return;
      const webhookUrl = String(this.config.submission?.webhookUrl || '').trim();
      if (!/^https:\/\//i.test(webhookUrl)) {
        throw new Error(
          this.setupMessage(
            'Die Übermittlung ist noch nicht eingerichtet. Bitte hinterlege die Make-Webhook-URL im Theme-Editor.',
            'Die Daten können gerade nicht sicher übermittelt werden. Bitte versuche es später noch einmal oder melde dich kurz bei uns.'
          )
        );
      }

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/plain, */*'
          },
          credentials: 'omit',
          signal: controller.signal,
          body: JSON.stringify(data)
        });
        if (!response.ok) {
          throw new Error(
            this.setupMessage(
              `Die Übermittlung wurde mit Status ${response.status} abgelehnt.`,
              'Die Daten konnten gerade nicht sicher übermittelt werden. Bitte versuche es später noch einmal.'
            )
          );
        }
        const responseText = await response.text();
        let result;
        try {
          result = JSON.parse(responseText);
        } catch (_) {
          throw new Error(
            this.setupMessage(
              'Das Make-Szenario hat keine gültige JSON-Bestätigung zurückgegeben.',
              'Die Daten konnten gerade nicht eindeutig bestätigt werden. Bitte versuche es später noch einmal.'
            )
          );
        }
        if (result?.ok !== true || result?.submissionId !== data.submissionId) {
          throw new Error(
            this.setupMessage(
              'Die Make-Bestätigung passt nicht zur gesendeten Vorgangs-ID.',
              'Die Daten konnten gerade nicht eindeutig bestätigt werden. Bitte versuche es später noch einmal.'
            )
          );
        }
        this.sentMakeStatuses.add(makeStatusKey);
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error('Die Übermittlung hat zu lange gedauert. Bitte versuche es noch einmal.');
        }
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    setupMessage(internalMessage, customerMessage) {
      return this.config.submission?.showInternalNotices === false
        ? customerMessage
        : internalMessage;
    }

    createSubmissionId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `fe-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    restoreSubmissionId() {
      const now = Date.now();
      // Einmalige Bereinigung der bisherigen ID, die nach einem Checkout
      // versehentlich bis zu zwölf Stunden wiederverwendet werden konnte.
      try {
        window.sessionStorage.removeItem(LEGACY_SUBMISSION_ID_KEY);
      } catch (_) {
        // Submission still works when storage is unavailable.
      }
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(SUBMISSION_ID_KEY) || 'null');
        if (
          stored?.id &&
          Number.isFinite(Number(stored.createdAt)) &&
          now - Number(stored.createdAt) < SUBMISSION_ID_MAX_AGE_MS
        ) {
          return String(stored.id);
        }
      } catch (_) {
        // A fresh ID is used when session storage is unavailable or invalid.
      }

      const id = this.createSubmissionId();
      try {
        window.sessionStorage.setItem(SUBMISSION_ID_KEY, JSON.stringify({ id, createdAt: now }));
      } catch (_) {
        // Submission still works when storage is unavailable.
      }
      return id;
    }

    clearSubmissionId() {
      try {
        window.sessionStorage.removeItem(SUBMISSION_ID_KEY);
        window.sessionStorage.removeItem(LEGACY_SUBMISSION_ID_KEY);
      } catch (_) {
        // Submission still works when storage is unavailable.
      }
    }

    resetSubmissionId() {
      this.clearSubmissionId();
      this.submissionId = this.restoreSubmissionId();
      this.startedAt = Date.now();
      this.sentMakeStatuses.clear();
    }

    setSubmittingState(label) {
      this.nextButton.disabled = Boolean(label);
      if (this.backButton) this.backButton.disabled = Boolean(label);
      if (label) this.nextButton.textContent = label;
      else this.updateNavigation();
    }

    visibleVideoShell(step) {
      return [...step.querySelectorAll('[data-fe-video-shell]')].find(
        (shell) => !shell.closest('[hidden]')
      );
    }

    activateVideo(step) {
      const shell = this.visibleVideoShell(step);
      if (!shell || shell.dataset.feHasVideo !== 'true') return null;
      const video = shell.querySelector('[data-fe-video]');
      if (!video) return null;
      if (video.dataset.feLoaded !== 'true') {
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
        });
        video.dataset.feLoaded = 'true';
        video.load();
      }
      return shell;
    }

    autoplayVideo(step) {
      const shell = this.activateVideo(step);
      if (shell) this.playVideo(shell);
    }

    playVideo(shell) {
      if (!shell || shell.dataset.feHasVideo !== 'true') return;
      const video = shell.querySelector('[data-fe-video]');
      if (!video) return;

      if (video.dataset.feLoaded !== 'true') {
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
        });
        video.dataset.feLoaded = 'true';
        video.load();
      }

      shell.classList.add('is-expanded', 'is-playing');
      video.controls = true;
      video.muted = false;
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          video.muted = true;
          const mutedPlayPromise = video.play();
          if (mutedPlayPromise?.catch) {
            mutedPlayPromise.catch(() => {
              video.muted = false;
              video.controls = false;
              shell.classList.remove('is-playing', 'is-expanded');
            });
          }
        });
      }
    }

    pauseAllVideos() {
      this.root.querySelectorAll('[data-fe-video]').forEach((video) => {
        if (!video.paused) video.pause();
        try {
          video.currentTime = 0;
        } catch (_) {
          // Some browsers reject currentTime before metadata is loaded.
        }
        video.controls = false;
        video.closest('[data-fe-video-shell]')?.classList.remove('is-playing', 'is-expanded');
      });
    }

    storeSubmitState(state) {
      try {
        window.sessionStorage.setItem(SUBMIT_STATE_KEY, JSON.stringify(state));
      } catch (_) {
        // Submission still works when storage is unavailable.
      }
    }

    readSubmitState() {
      try {
        const state = JSON.parse(window.sessionStorage.getItem(SUBMIT_STATE_KEY) || 'null');
        window.sessionStorage.removeItem(SUBMIT_STATE_KEY);
        return state;
      } catch (_) {
        return null;
      }
    }

    showCompletion(state) {
      this.startScreen.hidden = true;
      this.flow.hidden = false;
      this.form.hidden = true;
      this.root.querySelector('.fe-progress')?.setAttribute('hidden', '');
      this.completeScreen.hidden = false;
      this.setVideoSlot('complete', state?.kind === 'contact' ? 'complete_contact' : 'complete');

      const copy = this.root.querySelector('[data-fe-complete-copy]');
      if (copy) {
        if (state?.kind === 'bf17') {
          copy.textContent =
            `Für BF17 findet keine Onlinezahlung statt. Komm bitte gemeinsam mit deiner gesetzlichen Vertretung in die Filiale ${state.branch || 'deiner Wahl'}. Dort schließen wir den Ausbildungsvertrag ab${state.base ? ` und der Grundbetrag von ${state.base} wird bezahlt` : ''}.`;
        } else if (state?.kind === 'minor') {
          copy.textContent =
            `Da du noch nicht volljährig bist, findet keine Onlinezahlung statt. Komm bitte gemeinsam mit deiner gesetzlichen Vertretung in die Filiale ${state.branch || 'deiner Wahl'}. Dort schließen wir den Ausbildungsvertrag ab${state.base ? ` und der Grundbetrag von ${state.base} wird bezahlt` : ''}.`;
        } else {
          copy.textContent =
            'Vielen Dank für deine Anfrage! Unser Team prüft deine Angaben und meldet sich bei dir, um die passende Ausbildung und die nächsten Schritte persönlich zu besprechen.';
        }
      }

      this.autoplayVideo(this.completeScreen);
      window.scrollTo({ top: 0 });
    }
  }

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.feInitialized === 'true') return;
      root.dataset.feInitialized = 'true';
      new FeVoranmeldung(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
